import type { ReactNode } from 'react';
import { useSite } from '../ui/context';
import { duration, money } from '../ui/time';
import { imageValue } from '../theme/theme';
import { hoursRows } from './hours';
import {
  BookingSlots,
  FooterSlots,
  GallerySlots,
  HeroSlots,
  HoursSlots,
  ServicesSlots,
  StorySlots,
} from './registry';
import type { ArrangedSection } from './registry';

/**
 * The blocks the registry names. Config chose which ones and in what order
 * (`arrangeSections`); this file is what they look like.
 *
 * Each component takes its own parsed slots, so the type of a hero's `align` is
 * checked by the compiler rather than asserted. `SectionView` re-parses through
 * the same schema the arranger used — cheap, and it keeps the switch free of
 * casts, which is what `no-explicit-any` is really asking for.
 */

export interface SectionContext {
  /** The booking flow, mounted wherever the `booking` block lands. */
  bookingFlow: ReactNode;
}

export function SectionView({
  section,
  ctx,
}: {
  section: ArrangedSection;
  ctx: SectionContext;
}): ReactNode {
  switch (section.type) {
    case 'hero':
      return <Hero slots={HeroSlots.parse(section.slots)} />;
    case 'services':
      return <Services slots={ServicesSlots.parse(section.slots)} />;
    case 'hours':
      return <Hours slots={HoursSlots.parse(section.slots)} />;
    case 'story':
      return <Story slots={StorySlots.parse(section.slots)} />;
    case 'gallery':
      return <Gallery slots={GallerySlots.parse(section.slots)} />;
    case 'booking':
      return <BookingBlock slots={BookingSlots.parse(section.slots)} flow={ctx.bookingFlow} />;
    case 'footer':
      return <Footer slots={FooterSlots.parse(section.slots)} />;
  }
}

/* ------------------------------------------------------------------ */
/* Rung 4 — content zones                                             */
/* ------------------------------------------------------------------ */

/**
 * **The only `dangerouslySetInnerHTML` in this application.**
 *
 * Spec §6.4.3 rung 4: a tenant's own words and pictures in defined slots, as
 * "sanitized HTML/markdown (no scripts, **server-sanitized**)". The sanitizer is
 * the server's, on the write path, in the admin console — not here, and not on
 * the read path, because a client-side sanitizer would be a second
 * implementation of a security decision and the wrong side of the boundary to
 * put it on.
 *
 * That sanitizer **does not exist yet** (it lands with the admin console's theme
 * editor; noted in the report for this change). Until it does, and after it
 * does, this component carries a tripwire: a zone whose bytes look script-shaped
 * is rendered as *text*, visibly, with a console warning. It is not a sanitizer
 * and must never be mistaken for one — a determined payload will pass a regexp.
 * It is a smoke alarm on the one path where a tenant's bytes become a page's
 * markup, and it fails toward "the guest sees angle brackets" rather than
 * "the guest's browser runs a stranger's script".
 *
 * Everything else this bundle renders from config goes through ordinary JSX and
 * is escaped by React, which is why every other slot is plain text.
 */
const SCRIPT_SHAPED =
  /<\s*(script|iframe|object|embed|form|link|meta|style)\b|\son\w+\s*=|javascript:|data:text\/html/i;

export function RichText({ html, className }: { html: string; className?: string }): ReactNode {
  if (html === '') return null;

  if (SCRIPT_SHAPED.test(html)) {
    console.warn('[sections] a content zone looked script-shaped; it was rendered as text.');
    return <div className={className}>{html}</div>;
  }

  // Server-sanitized content zone. See the note above before touching this.
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ------------------------------------------------------------------ */
/* The blocks                                                          */
/* ------------------------------------------------------------------ */

function Hero({ slots }: { slots: ReturnType<typeof HeroSlots.parse> }): ReactNode {
  const { site, theme } = useSite();
  const background =
    slots.image !== '' && /^https:\/\//.test(slots.image)
      ? `url("${slots.image}")`
      : imageValue(theme.tokens, 'image.hero');
  const logo = imageValue(theme.tokens, 'image.logo');

  return (
    <header
      className={`hero hero--${slots.align} ${background === null ? 'hero--plain' : ''}`}
      style={background === null ? undefined : { backgroundImage: background }}
    >
      <div className="hero__inner">
        {logo === null ? null : (
          <div
            className="hero__logo"
            style={{ backgroundImage: logo }}
            role="img"
            aria-label={site.name}
          />
        )}
        {/* The tenant's *name* is the fallback headline, and it comes from
            `/api/site` rather than from config — so a site with no branding row
            at all still says who it is. */}
        <h1 className="hero__headline">{slots.headline === '' ? site.name : slots.headline}</h1>
        {slots.sub === '' ? null : <p className="hero__sub">{slots.sub}</p>}
        <a className="button button--primary hero__cta" href="#book">
          {slots.cta_label}
        </a>
      </div>
    </header>
  );
}

function Services({ slots }: { slots: ReturnType<typeof ServicesSlots.parse> }): ReactNode {
  const { site, services } = useSite();

  return (
    <section className="section services" aria-labelledby="services-title">
      <h2 className="section__title" id="services-title">
        {slots.headline}
      </h2>
      {slots.note === '' ? null : <p className="section__note">{slots.note}</p>}

      {services.length === 0 ? (
        <p className="section__empty">Nothing is bookable online at the moment.</p>
      ) : (
        <ul className={`services__list services__list--${slots.layout}`}>
          {services.map((service) => (
            <li className="service" key={service.service_id}>
              <div className="service__head">
                <h3 className="service__name">{service.name}</h3>
                {/* The catalog's price, displayed. Never sent back — see
                    `wire.ts`'s `CreateBooking`. */}
                {slots.show_price ? (
                  <span className="service__price">
                    {money(service.price_cents, site.currency)}
                  </span>
                ) : null}
              </div>
              {service.description === '' ? null : (
                <p className="service__description">{service.description}</p>
              )}
              {service.details.length === 0 ? null : (
                <ul className="service__details">
                  {service.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              )}
              {slots.show_duration ? (
                <p className="service__meta">{duration(service.duration_min)}</p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Two sources, and the precedence between them is the decision.
 *
 * The default is the **book's** week, projected by `/api/site` from the same
 * `tenant_config.hours` the scheduling engine reads, so the page cannot drift
 * from what is actually bookable. Typed `rows` override it, because an owner
 * who wrote "Sun–Mon closed, kitchen till 9" said something a table cannot, and
 * a block that ignored them would have taken a capability away to add one.
 */
function Hours({ slots }: { slots: ReturnType<typeof HoursSlots.parse> }): ReactNode {
  const { site } = useSite();
  const rows = slots.rows.length > 0 ? slots.rows : hoursRows(site.hours);
  if (rows.length === 0 && slots.note === '') return null;

  return (
    <section className="section hours" aria-labelledby="hours-title">
      <h2 className="section__title" id="hours-title">
        {slots.headline}
      </h2>
      <dl className="hours__rows">
        {rows.map((row) => (
          <div className="hours__row" key={`${row.label}|${row.value}`}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
      {slots.note === '' ? null : <p className="section__note">{slots.note}</p>}
      {/* Stated rather than assumed: every time on this page is this clock. */}
      <p className="hours__zone">All times {site.timezone.replace(/_/g, ' ')}.</p>
    </section>
  );
}

function Story({ slots }: { slots: ReturnType<typeof StorySlots.parse> }): ReactNode {
  if (slots.body === '' && slots.body_html === '') return null;

  return (
    <section className="section story" aria-labelledby="story-title">
      <h2 className="section__title" id="story-title">
        {slots.headline}
      </h2>
      {slots.body_html === '' ? (
        <p className="story__body">{slots.body}</p>
      ) : (
        <RichText className="story__body zone" html={slots.body_html} />
      )}
      {slots.image === '' || !/^https:\/\//.test(slots.image) ? null : (
        <img className="story__image" src={slots.image} alt="" loading="lazy" />
      )}
    </section>
  );
}

function Gallery({ slots }: { slots: ReturnType<typeof GallerySlots.parse> }): ReactNode {
  const images = slots.images.filter((image) => /^https:\/\//.test(image.src));
  if (images.length === 0) return null;

  return (
    <section className="section gallery" aria-labelledby="gallery-title">
      {slots.headline === '' ? null : (
        <h2 className="section__title" id="gallery-title">
          {slots.headline}
        </h2>
      )}
      <ul className="gallery__grid">
        {images.map((image) => (
          <li key={image.src}>
            {/* `alt` is a required slot even when empty: a decorative photograph
                says so with `alt=""`, and the contract makes that a decision the
                tenant records rather than one this bundle guesses. */}
            <img src={image.src} alt={image.alt} loading="lazy" />
            {image.caption === '' ? null : <figcaption>{image.caption}</figcaption>}
          </li>
        ))}
      </ul>
    </section>
  );
}

function BookingBlock({
  slots,
  flow,
}: {
  slots: ReturnType<typeof BookingSlots.parse>;
  flow: ReactNode;
}): ReactNode {
  return (
    <section className="section booking" id="book" aria-labelledby="booking-title">
      <h2 className="section__title" id="booking-title">
        {slots.headline}
      </h2>
      {slots.note === '' ? null : <p className="section__note">{slots.note}</p>}
      {flow}
    </section>
  );
}

function Footer({ slots }: { slots: ReturnType<typeof FooterSlots.parse> }): ReactNode {
  const { site } = useSite();

  return (
    <footer className="section footer">
      <p className="footer__name">{site.name}</p>
      {slots.rows.length === 0 ? null : (
        <dl className="footer__rows">
          {slots.rows.map((row) => (
            <div key={`${row.label}|${row.value}`}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {slots.note === '' ? null : <p className="footer__note">{slots.note}</p>}
      <RichText className="footer__zone zone" html={slots.html} />
    </footer>
  );
}
